import { useEffect, useRef } from 'react';

/**
 * One Back = one step (ux-screens §6.2). Every open layer (modal, sheet, full screen, dialogue, mini game,
 * "Sếp tới!") registers a handler while it is mounted; Esc, the Android back button and (on the web) the
 * browser's Back button run only the newest.
 */
const stack: { run: () => void }[] = [];
const listeners = new Set<() => void>();

function changed(): void {
  for (const fn of listeners) fn();
}

export function pushBackHandler(run: () => void): () => void {
  const entry = { run };
  stack.push(entry);
  changed();
  return () => {
    const i = stack.indexOf(entry);
    if (i >= 0) {
      stack.splice(i, 1);
      changed();
    }
  };
}

/** Runs the top handler. False when no layer is open (the caller falls through to the scene / exit). */
export function runBack(): boolean {
  const top = stack.at(-1);
  if (!top) return false;
  top.run();
  return true;
}

/** Registers `handler` as a Back step while mounted (and `active`). Later mounts sit on top. */
export function useBack(handler: () => void, active = true): void {
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(() => {
    if (!active) return;
    return pushBackHandler(() => {
      ref.current();
    });
  }, [active]);
}

const MARK = 'lct-layer';

/**
 * Web only (ux-screens §6.2): while any layer is open, one extra history entry sits on top, so the browser's
 * Back button (or Android Chrome's back swipe) closes the top layer instead of leaving the game. Closing the
 * last layer from the UI takes that entry away again. Returns a detach function.
 */
export function attachBrowserHistory(win: Window = window): () => void {
  let pushed = win.history.state === MARK;
  let skipPop = false;
  const sync = () => {
    if (stack.length > 0 && !pushed) {
      win.history.pushState(MARK, '');
      pushed = true;
    } else if (stack.length === 0 && pushed) {
      pushed = false;
      skipPop = true;
      win.history.back();
    }
  };
  const onPop = () => {
    pushed = win.history.state === MARK;
    if (skipPop) {
      // Our own back() after the last layer closed (re-push if a new layer opened meanwhile).
      skipPop = false;
      sync();
      return;
    }
    if (stack.length === 0) return;
    runBack();
    // Layers unmount on React's next commit: re-check once it has happened.
    setTimeout(sync, 0);
  };
  listeners.add(sync);
  win.addEventListener('popstate', onPop);
  sync();
  return () => {
    listeners.delete(sync);
    win.removeEventListener('popstate', onPop);
  };
}
